import { MimeType } from '@pressreader/types';

import { ICatalogNavGroup } from './catalog.navgroups';

enum ContributorRole {
    AUTHOR = 'A01',
    NARRATOR = 'E07',
}

interface ISearchBooksParams {
    query?: string;
    language?: string[];
    categoryPath?: string;
    flag?: string[];
}

type LastOpenedAs = 'none' | 'Sample' | 'FullOrJustPurchased';

interface IBook {
    id: number;
    prn: string;
    isbn: string;
    title: string;
    subtitle: string;
    description: string;
    language: string;
    language2: string;
    pageCount: number;
    imageId: string;
    authors: IAuthor[];
    publicationDate: Date | null;
    publisher: string;
    series: ISeries[];
    sample?: IBookContent;
    lastOpenedAs: LastOpenedAs;
    slug: string;
    isFree: boolean;
    isSponsored: boolean;
    showSampleRibbon?: boolean;
    supplier: string;
}

interface IBookDetails extends IBook {
    genres: IGenre[];
    biographicalNotes: string[];
    reviews: IReview[];
    content?: IBookContent;
    drm: BookDRMFeaturesConfig | null;
}

interface IBookContent {
    contentId: string;
    contentType: MimeType;
    size: number;
}

interface IBookContent {
    contentId: string;
    contentType: MimeType;
    size: number;
}

interface IGenre {
    id: number;
    displayName: string;
    slug: string | null;
    isFeatured: boolean;
    path: string | null;
    sortOrder: number;
    parentId: number | null;
    parent: IGenre | null;
    ancestors: number[] | null;
}

interface IAuthor {
    id: number;
    name: string;
    slug: string;
}

interface IReview {
    author: string;
    text: string;
}

interface ISeries {
    id: number;
    title: string;
    subtitle: string;
    slug: string;
    number: string;
}

interface IBookGenresTree {
    /** `null` when no genre path is selected (top-level category listings). */
    root: IGenre | null;
    genres: IGenre[];
    /** `true` when the child-category list could not be retrieved. Distinct from an empty `genres`,
     *  which means the catalog answered with none. */
    unavailable?: boolean;
}

/** Fields stay `null` until the books handlers populate them (non-books views never do). */
interface IBookViewParams {
    availBookGenres: IBookGenresTree | null;
    authorInfo: IAuthor | null;
    seriesInfo: ISeries | null;
    pathCategories: ICatalogNavGroup[] | null;
    filters: ISearchBooksParams | null;
    catalogContainsBooks: boolean;
}

interface IBookReadingPosition {
    href?: string;
    partialCfi?: string;
    position: number;
    relativePosition: number;
    type?: string;
    deviceName?: string;
}

interface IBookmark {
    title: string;
    text: string;
    pageLabel: string;
    createdAt: Date;
    startLocation: IBookReadingPosition;
    endLocation: IBookReadingPosition;
}

interface IBookBookmarks {
    bookId: number;
    bookmarks: IBookmark[];
    timestamp: Date;
}

interface BookDRMFeatureContentCopyingConfig {
    enabled: boolean;
    numberOfCharacters?: number;
}

interface BookDRMFeaturePrintContentConfig {
    enabled: boolean;
}

type BookDRMFeaturesConfig = Partial<{
    print: BookDRMFeaturePrintContentConfig;
    contentCopying: BookDRMFeatureContentCopyingConfig;
}>;

export {
    ContributorRole,
    BookDRMFeaturesConfig as DRMFeaturesConfig,
    IAuthor,
    IBook,
    IBookBookmarks,
    IBookContent,
    IBookDetails,
    IBookGenresTree,
    IBookmark,
    IBookReadingPosition,
    IBookViewParams,
    IGenre,
    IReview,
    ISearchBooksParams,
    ISeries,
    LastOpenedAs,
};
